import React, { Fragment, useState } from "react";
import Swal from "sweetalert2";
import { bulkStoreEntriesApi } from "../../../../services/inventory";

// Reconciliation of an Issued upload, item by item. Nothing is saved until the
// super-admin presses an item's Update button; that saves only that item's rows
// (the server checks the balance again first).
//
// rows: the parsed sheet rows (sent again for the detail view and the update)
// data: the preview response { notFound, skus: [{ skuId, skuName, measurement,
//       before, after, newRows, duplicates, errors }] }

const TYPE_LABEL = { add: "Received", issued: "Issued", return: "Returned", deduct: "Deducted" };
const showDate = (d) => (d ? d.split("-").reverse().join("-") : "");
const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN"));

const StockIssueReconciliation = ({ rows, data, onDone }) => {
  const [skus, setSkus] = useState(data.skus || []);
  const [open, setOpen] = useState(null);
  const [entries, setEntries] = useState({}); // skuId -> entries[] | "loading" | "error"
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState({}); // skuId -> entries saved

  const issuedRows = (s) => rows.filter((r) => s.newRows.includes(r.row));
  const fileQty = (s) => issuedRows(s).reduce((t, r) => t + (Number(r.qty) || 0), 0);

  // re-run the preview for one item (fresh numbers + its full entry list)
  const refresh = async (skuId) => {
    setEntries((m) => ({ ...m, [skuId]: "loading" }));
    try {
      const res = await bulkStoreEntriesApi(rows, { mode: "preview", skuId, detailSkuId: skuId });
      const fresh = res.data.skus.find((s) => s.skuId === skuId);
      if (fresh) setSkus((list) => list.map((s) => (s.skuId === skuId ? fresh : s)));
      setEntries((m) => ({ ...m, [skuId]: fresh?.entries || [] }));
    } catch {
      setEntries((m) => ({ ...m, [skuId]: "error" }));
    }
  };

  const toggle = (skuId) => {
    if (open === skuId) return setOpen(null);
    setOpen(skuId);
    if (!Array.isArray(entries[skuId])) refresh(skuId);
  };

  const update = async (s) => {
    const ok = await Swal.fire({
      icon: "question",
      title: `Update ${s.skuName}?`,
      html: `${s.newRows.length} issue entr${s.newRows.length === 1 ? "y" : "ies"}, total ${fmt(fileQty(s))} ${s.measurement}.<br/>Stock goes from <b>${fmt(s.before)}</b> to <b>${fmt(s.after)}</b>.`,
      showCancelButton: true,
      confirmButtonText: "Update",
    });
    if (!ok.isConfirmed) return;
    setSaving(s.skuId);
    try {
      const res = await bulkStoreEntriesApi(rows, { skuId: s.skuId });
      setSaved((m) => ({ ...m, [s.skuId]: res.data.created }));
      setEntries((m) => ({ ...m, [s.skuId]: undefined }));
      if (open === s.skuId) setOpen(null);
      if (onDone) onDone(res.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Not updated",
        text: err?.response?.data?.message || "Could not save this item.",
      });
      await refresh(s.skuId);
      setOpen(s.skuId);
    } finally {
      setSaving(null);
    }
  };

  const status = (s) => {
    if (saved[s.skuId] !== undefined) return <span className="badge bg-success">Updated ({saved[s.skuId]})</span>;
    if (s.errors.length) {
      const short = s.errors.some((e) => /insufficient|below zero/i.test(e.message));
      return <span className="badge bg-danger">{short ? "Short stock" : "Fix rows"}</span>;
    }
    if (!s.newRows.length) return <span className="badge bg-secondary">Already entered</span>;
    return <span className="badge bg-info text-dark">Ready</span>;
  };

  const canUpdate = (s) => saved[s.skuId] === undefined && !s.errors.length && s.newRows.length > 0;

  return (
    <>
      {data.notFound?.length > 0 && (
        <div className="alert alert-warning py-2 mb-2 small">
          <b>SKU not in our stock</b> — these rows are not included:{" "}
          {data.notFound.map((n) => `row ${n.row} (${n.skuName || "blank"})`).join(", ")}
        </div>
      )}
      <p className="small text-muted mb-2">
        Nothing is saved yet. Click an item to see all its entries, then press Update for each item you are satisfied with.
      </p>
      <div className="table-responsive" style={{ maxHeight: 480 }}>
        <table className="table table-sm table-bordered mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Item</th>
              <th className="text-end">Stock now</th>
              <th className="text-end">Issued in file</th>
              <th className="text-end">Stock after</th>
              <th>Status</th>
              <th style={{ width: 100 }} />
            </tr>
          </thead>
          <tbody>
            {skus.map((s) => (
              <Fragment key={s.skuId}>
                <tr>
                  <td>
                    <button type="button" className="btn btn-link btn-sm p-0 text-start" onClick={() => toggle(s.skuId)}>
                      <i className={`fa fa-caret-${open === s.skuId ? "down" : "right"} me-1`} />
                      {s.skuName}
                    </button>
                    {s.measurement && <span className="text-muted small"> ({s.measurement})</span>}
                    {s.duplicates.length > 0 && (
                      <div className="small text-muted">{s.duplicates.length} row(s) already entered in stock</div>
                    )}
                  </td>
                  <td className="text-end">{fmt(s.before)}</td>
                  <td className="text-end">{s.newRows.length ? fmt(fileQty(s)) : "—"}</td>
                  <td className={`text-end ${s.after < 0 ? "text-danger fw-bold" : ""}`}>{fmt(s.after)}</td>
                  <td>{status(s)}</td>
                  <td>
                    {canUpdate(s) && (
                      <button
                        type="button"
                        className="btn btn-primary btn-xs btn-sm py-0"
                        disabled={saving !== null}
                        onClick={() => update(s)}
                      >
                        {saving === s.skuId ? "Saving…" : "Update"}
                      </button>
                    )}
                  </td>
                </tr>
                {open === s.skuId && (
                  <tr>
                    <td colSpan={6} className="bg-light">
                      {[...s.errors, ...s.duplicates].length > 0 && (
                        <ul className="small mb-2 ps-3">
                          {s.errors.map((e, i) => (
                            <li key={`e${i}`} className="text-danger">Row {e.row}: {e.message}</li>
                          ))}
                          {s.duplicates.map((e, i) => (
                            <li key={`d${i}`} className="text-muted">Row {e.row}: {e.message}</li>
                          ))}
                        </ul>
                      )}
                      {entries[s.skuId] === "loading" && <div className="small text-muted">Loading entries…</div>}
                      {entries[s.skuId] === "error" && <div className="small text-danger">Could not load the entries.</div>}
                      {Array.isArray(entries[s.skuId]) && (
                        <div className="table-responsive" style={{ maxHeight: 320 }}>
                          <table className="table table-sm table-bordered mb-0 bg-white small">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Supplier / Contractor</th>
                                <th>Bill / Voucher No</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end">Balance</th>
                                <th>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entries[s.skuId].map((e, i) => (
                                <tr key={i} className={e.isNew ? "table-warning" : ""}>
                                  <td>
                                    {showDate(e.date)}
                                    {e.isNew && <span className="badge bg-warning text-dark ms-1">file row {e.row}</span>}
                                  </td>
                                  <td>{TYPE_LABEL[e.entryType] || e.entryType}</td>
                                  <td>{e.party || "—"}</td>
                                  <td>{e.invoiceNo || "—"}</td>
                                  <td className="text-end">
                                    {e.entryType === "add" || e.entryType === "return" ? "+" : "−"}
                                    {fmt(e.qty)}
                                  </td>
                                  <td className={`text-end ${e.balance < 0 ? "text-danger fw-bold" : ""}`}>{fmt(e.balance)}</td>
                                  <td>{e.remarks || ""}</td>
                                </tr>
                              ))}
                              {!entries[s.skuId].length && (
                                <tr>
                                  <td colSpan={7} className="text-muted">No entries.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!skus.length && (
              <tr>
                <td colSpan={6} className="text-muted">No items from the file match our stock.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default StockIssueReconciliation;
